import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { getWp, setWpStatus, setWpIdea, setSubtasks, downloadBlob } from '../../api/dev';
import { useSnackbar } from '../../context/SnackbarContext';
import { BugReportDialog } from './BugReportDialog';
import type { WorkPackageDetail, WpStatus } from '../../types/dev';

const STATUS_COLORS: Record<WpStatus | 'idea', string> = {
  done: '#43a047',
  in_progress: '#1976d2',
  upcoming: '#9e9e9e',
  blocked: '#e53935',
  idea: '#f57c00',
};

const STATUS_LABELS: Record<WpStatus | 'idea', string> = {
  done: '✅ Done',
  in_progress: '🔵 In Progress',
  upcoming: '⚪ Upcoming',
  blocked: '🔴 Blocked',
  idea: '💡 Idea',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

interface Props {
  wpNumber: string | null;
  onClose: () => void;
}

export function WpDetailDrawer({ wpNumber, onClose }: Props) {
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  const [localBugCount, setLocalBugCount] = useState<number | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const { data: wp, isLoading } = useQuery<WorkPackageDetail>({
    queryKey: ['dev', 'wp', wpNumber],
    queryFn: () => getWp(wpNumber!),
    enabled: wpNumber != null,
  });

  const bugCount = localBugCount ?? wp?.bug_count ?? 0;

  const handleStatusChange = async (status: WpStatus) => {
    setStatusAnchorEl(null);
    if (!wp) return;
    try {
      await setWpStatus(wp.id, status);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wp', wp.wp_number] });
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleIdeaToggle = async () => {
    setStatusAnchorEl(null);
    if (!wp) return;
    try {
      await setWpIdea(wp.id, !wp.is_idea);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wp', wp.wp_number] });
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
    } catch {
      showSnackbar('Failed to update idea flag', 'error');
    }
  };

  const handleSubtaskToggle = async (idx: number) => {
    if (!wp?.subtasks) return;
    const updated = wp.subtasks.map((t, i) => (i === idx ? { ...t, done: !t.done } : t));
    try {
      await setSubtasks(wp.id, updated);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wp', wp.wp_number] });
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
    } catch {
      showSnackbar('Failed to update subtasks', 'error');
    }
  };

  const handleDownload = async () => {
    if (!wp) return;
    try {
      const blob = await downloadBlob(wp.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = wp.blob_filename ?? `${wp.wp_number}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Download failed', 'error');
    }
  };

  const handleBugReported = (count: number) => {
    setLocalBugCount(count);
    void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={wpNumber != null}
        onClose={onClose}
        PaperProps={{ sx: { width: 500 } }}
      >
        <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {wp && (
            <Stack spacing={2}>
              {/* Header */}
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    label={wp.wp_number}
                    size="small"
                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {wp.title}
                  </Typography>
                  <Tooltip title="Click to change status">
                    <Chip
                      label={STATUS_LABELS[wp.is_idea ? 'idea' : (wp.status as WpStatus)]}
                      size="small"
                      onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                      sx={{ bgcolor: STATUS_COLORS[wp.is_idea ? 'idea' : (wp.status as WpStatus)], color: '#fff', cursor: 'pointer' }}
                    />
                  </Tooltip>
                  <Menu
                    anchorEl={statusAnchorEl}
                    open={Boolean(statusAnchorEl)}
                    onClose={() => setStatusAnchorEl(null)}
                  >
                    <MenuItem onClick={() => void handleStatusChange('upcoming')}>⚪ Upcoming</MenuItem>
                    <MenuItem onClick={() => void handleStatusChange('in_progress')}>🔵 In Progress</MenuItem>
                    <MenuItem onClick={() => void handleStatusChange('done')}>✅ Done</MenuItem>
                    <MenuItem onClick={() => void handleStatusChange('blocked')}>🔴 Blocked</MenuItem>
                    <MenuItem onClick={() => void handleIdeaToggle()}>💡 Idea</MenuItem>
                  </Menu>
                </Stack>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Stack>

              {/* Description */}
              {wp.description && (
                <Typography variant="body2" color="text.secondary">
                  {wp.description}
                </Typography>
              )}

              {/* Subtasks */}
              {wp.subtasks && wp.subtasks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Subtasks — {wp.subtasks.filter((t) => t.done).length} / {wp.subtasks.length} completed
                  </Typography>
                  <Stack>
                    {wp.subtasks.map((task, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            checked={task.done}
                            onChange={() => void handleSubtaskToggle(idx)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{task.text}</Typography>}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Markdown blob */}
              {wp.has_blob && wp.blob_content && (
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Feature Document</Typography>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => void handleDownload()}
                    >
                      Download .md
                    </Button>
                  </Stack>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 400,
                      overflow: 'auto',
                      '& h1,h2,h3,h4': { mt: 1, mb: 0.5 },
                      '& p': { my: 0.5 },
                      '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 0.5, overflowX: 'auto' },
                      '& code': { fontFamily: 'monospace', fontSize: '0.85em' },
                    }}
                  >
                    <ReactMarkdown>{wp.blob_content}</ReactMarkdown>
                  </Box>
                </Box>
              )}

              {/* Bug tracker */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2">Bugs:</Typography>
                  {bugCount > 0 ? (
                    <Chip
                      icon={<BugReportIcon />}
                      label={bugCount}
                      size="small"
                      sx={{ bgcolor: '#c62828', color: '#fff' }}
                    />
                  ) : (
                    <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
                  )}
                  <Button
                    size="small"
                    startIcon={<BugReportIcon />}
                    color="error"
                    onClick={() => setBugDialogOpen(true)}
                  >
                    Report Bug
                  </Button>
                </Stack>
              </Box>

              {/* Dates */}
              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Generated</Typography>
                  <Typography variant="body2">{fmtDate(wp.generated_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Inserted</Typography>
                  <Typography variant="body2">{fmtDate(wp.inserted_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Solved</Typography>
                  <Typography variant="body2">{fmtDate(wp.solved_at)}</Typography>
                </Box>
              </Stack>
            </Stack>
          )}
        </Box>
      </Drawer>

      {wp && (
        <BugReportDialog
          open={bugDialogOpen}
          wpId={wp.id}
          wpNumber={wp.wp_number}
          wpTitle={wp.title}
          onClose={() => setBugDialogOpen(false)}
          onReported={handleBugReported}
        />
      )}
    </>
  );
}
